import React, { useMemo } from 'react';
import {
  Person,
  UpdateOwnProfileRequest,
  useAuthenticationContextRequired,
  usePeopleService
} from 'nekst-api';
import { InformationMessage, Separator } from 'features/nekst-widgets';
import {
  FormBlock,
  FormTemplate,
  SubmitButton,
  TextArea,
  Wysiwyg
} from 'ui-builder';
import { UpdatePersonFormFields } from '../People';
import useFeatureToggleService from 'feature-toggle';

function CompleteAccountMessage() {
  const authenticationContext = useAuthenticationContextRequired();

  if (authenticationContext && !authenticationContext.user.hasActiveAccount) {
    return (
      <InformationMessage>
        Thank you for registering! To complete your profile, please fill out the form below.
      </InformationMessage>
    );
  } else {
    return null;
  }
}

export function UpdateProfileForm(
  props: {
    hideTitle?: boolean,
    onAccountCompleted?: () => void,
  }
) {
  const authenticationContext = useAuthenticationContextRequired();
  const peopleService = usePeopleService();
  const featureToggleService = useFeatureToggleService();

  const isAccountCompleted = authenticationContext.user.hasActiveAccount;

  const teamRole = authenticationContext.user.teamRole;

  const initialData = useMemo(() => {
    const profile = authenticationContext.user.profile;

    const emailSignature = profile.emailSignature || '';

    let signatureValue = '';
    if (emailSignature.length > 0 && emailSignature[0] !== '<') {
      let signatureParts: string[] = [];
      if (emailSignature.includes("\r\n")) {
        signatureParts = profile.emailSignature?.split("\r\n") || [];
      } else {
        signatureParts = profile.emailSignature?.split("\n") || [];
      }


      if (signatureParts && signatureParts.length > 1) {
        signatureValue = signatureParts.map((part) => '<p>' + part + '</p>').join(' ');
      } else {
        signatureValue = emailSignature;
      }
    } else {
      signatureValue = emailSignature;
    }

    return {
      profile: {
        ...profile,
        emailSignature: signatureValue,
      },
      isDailyMailEnabled: authenticationContext.user._user.isDailyMailEnabled,
    }
  }, []);

  const signatureWidget = featureToggleService.isHtmlSignatureEnabled()
    ? (<TextArea source="profile.emailSignature" label="Email Signature" />)
    : (<Wysiwyg source="profile.emailSignature" label="Email Signature" maxImageWidth={500} />)

  return (
    <FormTemplate<UpdateOwnProfileRequest, Person>
      submitFormFunc={async (data) => {
        const result = await peopleService.updateOwnProfile(data);

        if (!isAccountCompleted) {
          props.onAccountCompleted?.();
        }

        return result;
      }}
      initialData={initialData}
      buttons={(
        <SubmitButton text="Update" />
      )}
      title={!props.hideTitle ? 'Profile' : undefined}
      validationSchema={() => peopleService.getUpdateValidationSchema(teamRole)}
    >
      <CompleteAccountMessage />
      <UpdatePersonFormFields person={authenticationContext.user} />
      <FormBlock>
        <Separator />
        {signatureWidget}
      </FormBlock>

    </FormTemplate>
  );
}
